import { z } from 'zod/v4';

/**
 * Body accepted by `POST /admin/users`. Faithful port of the legacy
 * `user.createUnScoped` JSON Schema
 * (`backend/src/2025-06/api-service/routes/schemas/v3/request/user.js`).
 * Unlike `create.ts` (the non-admin `POST /users`), `role` is required and
 * caller-supplied — {@link UsersAdminService.createUnscoped} authorizes it
 * against the invoker's own role. `isAdmin` is part of the legacy JSON
 * Schema but never read by the legacy handler/service — dropped here as
 * accepted-but-ignored noise.
 */
export const AdminCreateUserBodySchema = z.object({
  email: z.email().nullable().optional(),
  companyId: z.string().min(1).nullable().optional(),
  role: z.string().min(1),
  firstName: z
    .string()
    .min(1)
    .max(255)
    .regex(/^\S.*/, 'firstName must not start with whitespace'),
  lastName: z.string().nullable().optional(),
});

export type AdminCreateUserBody = z.infer<typeof AdminCreateUserBodySchema>;

/**
 * Row shape returned by `POST /admin/users` — the raw (unenriched) user row,
 * same as legacy `usersDB.getByIdUnScoped`. `isBlocked` is `boolean` (not
 * `number`, unlike the non-admin `create.ts` response): the legacy route's
 * JSON Schema declared it as `boolean` and fast-json-stringify coerced the
 * raw `0`/`1` on the way out, so the service converts it explicitly with
 * `Boolean()` to preserve that shape. `looseObject` because the underlying
 * `users` table has ~25 columns (including audit columns) and this response
 * doesn't need strict validation of all of them.
 */
export const AdminCreateUserResponseSchema = z.looseObject({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  primaryCompanyId: z.string().nullable(),
  langId: z.string().nullable(),
  isBlocked: z.boolean().nullable(),
  developmentTag: z.string().nullable(),
  intercomHash: z.string().nullable(),
  emailSha256: z.string().nullable(),
  avatarFileUrl: z.string().nullable(),
  originalAvatarFileUrl: z.string().nullable(),
});

export type AdminCreateUserResponse = z.infer<
  typeof AdminCreateUserResponseSchema
>;
